import React, { useState } from 'react';
import {
  Backdrop,
  ModalWrapper,
  ModalTitle,
  InputWrapper,
  Label,
  Input,
  Button,
  ModalActions,
} from '../Modal/modalStyle';
import Transition from '../Transition';
import styled from 'styled-components';

const Body = styled.p`
  font-size: 1.6rem;
  color: #444;
  margin: 1.2rem 0 0;
  line-height: 1.5;
  max-width: 380px;
`;

const ConfirmNote = styled.p`
  font-size: 1.4rem;
  color: #888;
  margin: 0.6rem 0 0;
`;

const ErrorNote = styled.p`
  font-size: 1.4rem;
  color: #e53e3e;
  margin: 0.8rem 0 0;
`;

interface SubmitModalProps {
  initialEmail: string;
  firstName: string;
  sessionId: string;
  /** Full conversation history — server uses this to write the canonical transcript */
  messages: Array<{ role: string; content: string }>;
  onDismiss: () => void;
  trigger: 'user' | 'agent' | 'idle';
}

type ModalState = 'idle' | 'submitting' | 'done' | 'error';

export default function SubmitModal({
  initialEmail,
  firstName,
  sessionId,
  messages,
  onDismiss,
  trigger,
}: SubmitModalProps) {
  const [email, setEmail] = useState(initialEmail);
  const [modalState, setModalState] = useState<ModalState>('idle');

  // Used only for copy branching — the agent-triggered path uses different wording.
  const triggeredByAgent = trigger === 'agent';

  const handleSubmit = async () => {
    setModalState('submitting');
    try {
      // 'idle' is a UI-side label — the Airtable Transmit_Trigger column
      // doesn't have it yet. Send 'abandon' for the wire payload (same
      // semantics: user walked away from the chat).
      const wireTrigger = trigger === 'idle' ? 'abandon' : trigger;
      const res = await fetch('/api/submit-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          firstName,
          email,
          trigger: wireTrigger,
          messages,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setModalState('done');
      } else {
        console.error('[SubmitModal] submit-session failed:', data.error);
        setModalState('error');
      }
    } catch (err) {
      console.error('[SubmitModal] Unexpected error:', err);
      setModalState('error');
    }
  };

  if (modalState === 'done') {
    return (
      <Backdrop onClick={onDismiss}>
        <Transition className="fade" enter timeout={500}>
          <ModalWrapper>
            <ModalTitle>You're all set!</ModalTitle>
            <Body>
              Your Ikigai responses have been submitted. We'll send your
              personalized diagram to <strong>{email}</strong> soon.
            </Body>
            <ModalActions>
              <Button onClick={onDismiss} primary>
                Close
              </Button>
            </ModalActions>
          </ModalWrapper>
        </Transition>
      </Backdrop>
    );
  }

  return (
    <Backdrop>
      <Transition className="fade" enter timeout={500}>
        <ModalWrapper>
          <ModalTitle>
            {triggeredByAgent ? 'Get Your Ikigai Diagram' : 'Submit My Responses'}
          </ModalTitle>
          <Body>
            {triggeredByAgent
              ? "It looks like your conversation is wrapping up! Confirm your email and we'll send your personalized Ikigai diagram."
              : "Confirm your email and we'll send your personalized Ikigai diagram when it's ready."}
          </Body>
          <InputWrapper>
            <Label>Your email address:</Label>
            <Input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              type="email"
              value={email}
              placeholder="your@email.com"
            />
            <ConfirmNote>We'll send your Ikigai diagram here.</ConfirmNote>
            {modalState === 'error' && (
              <ErrorNote>Something went wrong. Please try again.</ErrorNote>
            )}
          </InputWrapper>
          <ModalActions>
            <Button onClick={onDismiss} disabled={modalState === 'submitting'}>
              Not Now
            </Button>
            <Button
              onClick={handleSubmit}
              primary
              disabled={!email || modalState === 'submitting'}
            >
              {modalState === 'submitting' ? 'Sending… (this may take a few seconds)' : 'Send My Ikigai'}
            </Button>
          </ModalActions>
        </ModalWrapper>
      </Transition>
    </Backdrop>
  );
}
